import _ from 'lodash';
import { isEmptyVal, safeJSONParse } from '@/utils';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { partnerFeaturePropertiesTarget } from '@/views/config/partner-refer/helper/constant';
import { transferPlanCodeListToDiscountInfo } from '@/views/config/coupon/helper/pre-config';
import { logger } from '@/utils/log';

/**
 * @description replace config plan map price for landing page
 * @param p {PartnerReferBean}
 */
function replacePartnerPlanPrice(p = {}) {
  if (isEmptyVal(p?.planPrice)) return;

  p.planPrice = _.chain(p.planPrice)
    .keys()
    .reduce((pre, planCode) => {
      const currentPlan = _.get(p.planPrice, planCode, {});
      const matchPlanCodeGroupList = PLAN_PRICE_CHANGE_UTILS.getPlanListForPriceChange(planCode);

      if (matchPlanCodeGroupList) {
        return _.reduce(matchPlanCodeGroupList, (v, p) => {
          return { ...v, [p]: currentPlan };
        }, pre);
      }

      return {
        ...pre,
        [planCode]: currentPlan,
      };
    }, {})
    .value();
}

/**
 * @description For partner preload plan with pro Monthly/Annual
 * @param p {PartnerReferBean}
 */
function replacePartnerPreloadList(p) {
  if (isEmptyVal(p?.preloadPlan)) return;

  p.preloadPlan = _.reduce(p.preloadPlan, (pre, planCode) => {
    const matchPlanList = _.find(PLAN_PRICE_CHANGE_UTILS.planCodeGroupList, (planCodeList) => _.includes(planCodeList, planCode));

    if (!isEmptyVal(matchPlanList)) {
      return [...pre, ...matchPlanList];
    }

    return [...pre, planCode];
  }, []);
}

/**
 * @description need to be compatible with the one-dimensional array structure returned by the backend of the old version
 * @description need auto add zoom one pro Monthly/Annual group plan list
 * @description [['yearly_c50', 'masterclass']] -> [['yearly_c50', 'masterclass'], ['zo_pro_yearly', 'masterclass']]
 * @param p {PartnerReferBean}
 */
function replacePartnerEligiblePlans(p) {
  if (isEmptyVal(p?.rules?.eligiblePlanList)) return;

  // ['business_yearly_c50', 'yearly_c50', 'zo_pro_yearly', 'zonebizpzvu_yearly', 'zonebizpzuu_yearly', 'zonebizpzau_yearly']
  p.features.flattenEligiblePlanList = _.chain(p?.rules?.eligiblePlanList)
    .cloneDeep()
    .flattenDeep()
    .uniq()
    .value();

  // [['yearly_c50', 'masterclass']] -> [['yearly_c50', 'masterclass'], ['zo_pro_yearly', 'masterclass']]
  p.rules.eligiblePlanList = _.chain(p?.rules?.eligiblePlanList)
    .filter(Boolean)
    .map((item) => _.isString(item) ? [item] : item)
    .reduce((pre = [], planList = []) => {
      const matchProPlanCodeList = _.find(PLAN_PRICE_CHANGE_UTILS.planCodeGroupList, (planCodeList) => {
        return _.some(planCodeList, (planCode) => _.includes(planList, planCode));
      });

      // match increase plan cases, manually add other plan
      if (matchProPlanCodeList) {
        const matchPlanCode = _.find(matchProPlanCodeList, (p) => _.includes(planList, p));
        const otherPlanCode = _.find(matchProPlanCodeList, (p) => p !== matchPlanCode);
        const otherPlanList = _.reduce(planList, (v, p) => {
          return [...v, p === matchPlanCode ? otherPlanCode : p];
        }, []);

        return [...pre, planList, otherPlanList];
      }

      return [...pre, planList];
    }, [])
    .value();
}

function transferFeatureList2Object(featureList = []) {
  return _.reduce(featureList, (pre, item) => {
    const ruleCode = item?.ruleCode;
    const ruleValue = item?.ruleValue;
    const value = partnerFeaturePropertiesTarget.getFormatedValue(ruleCode, ruleValue);
    ruleCode && (pre[ruleCode] = value);
    return pre;
  }, {});
}

/**
 *
 * @param p {PartnerReferBean}
 */
function replacePartnerFeatures(p) {
  // legacy version the backend will format those rules
  const transferFeatureFromRules = _.pick(p.rules, [
    'needDistributionQuantity',
    'distributionDependPlanList',
  ]);

  const featureList2Object = transferFeatureList2Object(p.featureList);

  // If softBundleConfigV2 exists, rename it to softBundleConfig
  if (featureList2Object?.softBundleConfigV2) {
    featureList2Object.softBundleConfig = featureList2Object.softBundleConfigV2;
    delete featureList2Object.softBundleConfigV2;
  }

  p.features = { ...p.features, ...featureList2Object, ...transferFeatureFromRules };

  if (p.featureList) {
    delete p.featureList;
  }
}

/**
 * @param config{GlobalPageConfigBean}
 */
export const preConfigWithPartnerRefer = (config = {}) => {
  /** @type {PartnerReferBean} */
  const partnerReferral = config?.partnerReferral;

  if (isEmptyVal(partnerReferral)) return config;
  if (isEmptyVal(partnerReferral?.rules)) partnerReferral.rules = {};
  if (isEmptyVal(partnerReferral?.features)) partnerReferral.features = {};

  const { messageMap } = partnerReferral || {};
  const couponJsonString = config?.marketingCoupon;


  // will use marketingCoupon fieldValue to set refer bind with coupon node
  if (!isEmptyVal(couponJsonString)) {
    try {
      const couponData = safeJSONParse(couponJsonString);
      if (couponData) {
        couponData.defaultDiscountInfo = transferPlanCodeListToDiscountInfo(couponData?.defaultPlanCodeList);
      }
      partnerReferral.coupon = couponData;
      // Clear marketingCoupon to prevent duplicate processing in preConfigWithCampaign
      config.marketingCoupon = null;
    } catch (error) {
      logger.error('Failed to parse marketingCoupon for partnerReferral:', error);
      // Continue processing even if coupon parsing fails
    }
  }

  // Merge the fields in the message map into partner
  if (!isEmptyVal(messageMap)) {
    _.assign(partnerReferral, messageMap);
    delete partnerReferral.messageMap;
  }

  // For net new/free account with error partner referral code meta info, just show error message, don't care about some check rules
  if (!isEmptyVal(config?.partnerReferralErrorResult)) {
    logger.error('For net new/free account with error partner referral code meta info!!!');

    return {
      ...config,
      partnerReferral: {
        ...partnerReferral,
        coupon: null,
        rules: {},
      },
    };
  }

  // PRO INCREASE
  replacePartnerPreloadList(partnerReferral);
  replacePartnerPlanPrice(partnerReferral);
  replacePartnerEligiblePlans(partnerReferral);
  replacePartnerFeatures(partnerReferral);

  logger.info('parse partnerReferral', partnerReferral);

  return {
    ...config,
    partnerReferral,
  };
};
